import EntityType from '@/shared/enums/EntityType';
import { Visibility } from '@/lib/enums';
import addImpactByImpactType from './addImpactByImpactType';

export default function mapAggregateImpact({
  comparisonScenario,
  scenario,
  showBudget,
}) {
  if (!comparisonScenario) {
    return;
  }

  const impactMap = new Map();

  // Keep a record of the comparison entities.
  const originalActivityList = new Set(
    comparisonScenario.relationships?.get(EntityType.ACTIVITY)?.keys()
  );
  const originalRoleList = new Set(
    comparisonScenario.relationships?.get(EntityType.ROLE)?.keys()
  );

  // Keep a list of the scenario roles and groups to handle actions for roles
  // and groups without activities associated with them.
  const scenarioRoleList = new Set(
    scenario.relationships?.get(EntityType.ROLE)?.keys()
  );

  // Add actions connected to the current activities in the scenario. This
  // should capture most actions.
  [...scenario.relationships.get(EntityType.ACTIVITY).values()].forEach(
    (activity) => {
      const role =
        activity.owner_type === EntityType.ROLE
          ? scenario.relationships
              .get(EntityType.ROLE)
              ?.get(activity.owner_uuid)
          : null;

      const originalActivity = comparisonScenario.relationships
        ?.get(EntityType.ACTIVITY)
        ?.get(activity.uuid);
      const originalRole = comparisonScenario.relationships
        .get(EntityType.ROLE)
        ?.get(role?.uuid);

      // Remove the entities from their lists so we restrict additional data
      // changes to those entities which haven't been accounted for.
      originalActivityList.delete(originalActivity?.uuid);

      if (activity.__visibility !== Visibility.FULL) {
        return;
      }

      // Remove the entities from their lists so we restrict additional data
      // changes to those entities which haven't been accounted for.
      originalRoleList.delete(originalRole?.uuid);
      scenarioRoleList.delete(role?.uuid);

      const impacts = [];

      impacts.push(
        ...addImpactByImpactType({
          activity,
          impactMap,
          comparisonScenario,
          originalActivity,
          originalRole,
          role,
          scenario,
          showBudget,
        })
      );

      for (let i = 0; i < impacts?.length; i++) {
        const impact = impacts[i];
        impactMap.set(impact.type, { ...impact });
      }
    }
  );

  // Loop through the activities which are present in the comparison but not
  // in the scenario.
  [...originalActivityList].forEach((id) => {
    const originalActivity = comparisonScenario.relationships
      .get(EntityType.ACTIVITY)
      ?.get(id);
    const originalRole =
      originalActivity.owner_type === EntityType.ROLE
        ? comparisonScenario.relationships
            .get(EntityType.ROLE)
            ?.get(originalActivity?.owner_uuid)
        : null;
    const role = scenario?.relationships
      .get(EntityType.ROLE)
      ?.get(originalRole?.uuid);

    originalActivityList.delete(originalActivity?.uuid);

    if (originalActivity?.__visibility !== Visibility.FULL) {
      return;
    }

    // Remove the entities from their lists so we restrict additional data
    // changes to those entities which haven't been accounted for.
    originalActivityList.delete(originalActivity?.uuid);
    originalRoleList.delete(originalRole?.uuid);
    scenarioRoleList.delete(role?.uuid);

    const impacts = [];

    impacts.push(
      ...addImpactByImpactType({
        impactMap,
        comparisonScenario,
        originalActivity,
        originalRole,
        role,
        scenario,
        showBudget,
      })
    );

    for (let i = 0; i < impacts?.length; i++) {
      const impact = impacts[i];
      impactMap.set(impact.type, { ...impact });
    }
  });

  // Loop through scenario roles that don't have any activities associated with
  // them.
  [...scenarioRoleList].forEach((id) => {
    const role = scenario.relationships.get(EntityType.ROLE)?.get(id);
    const originalRole = comparisonScenario.relationships
      .get(EntityType.ROLE)
      ?.get(id);

    scenarioRoleList.delete(role?.uuid);

    if (role?.__visibility !== Visibility.FULL) {
      return;
    }

    // Remove the entities from their lists so we restrict additional data
    // changes to those entities which haven't been accounted for.
    scenarioRoleList.delete(role?.uuid);
    originalRoleList.delete(originalRole?.uuid);

    const impacts = [];

    impacts.push(
      ...addImpactByImpactType({
        impactMap,
        comparisonScenario,
        originalRole,
        role,
        scenario,
        showBudget,
      })
    );

    for (let i = 0; i < impacts?.length; i++) {
      const impact = impacts[i];
      impactMap.set(impact.type, { ...impact });
    }
  });

  // Loop through any comparison roles that didn't have activities associated
  // with them.
  [...originalRoleList].forEach((id) => {
    const originalRole = comparisonScenario.relationships
      .get(EntityType.ROLE)
      ?.get(id);

    if (originalRole?.__visibility !== Visibility.FULL) {
      return;
    }

    const impacts = [];

    impacts.push(
      ...addImpactByImpactType({
        impactMap,
        comparisonScenario,
        originalRole,
        scenario,
        showBudget,
      })
    );

    for (let i = 0; i < impacts?.length; i++) {
      const impact = impacts[i];
      impactMap.set(impact.type, { ...impact });
    }
  });

  return impactMap;
}
